import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./EditAreasOfPracticeModal.css";
import { createPortal } from "react-dom";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CloseModal from "../../Atomos/Icons/EditMode/CloseModal";
import CancelIcon from "../../Atomos/Icons/Close";
import SaveChanges from "../../Atomos/Icons/EditMode/SaveChanges";
import {
  memberModalPrimaryButtonClassName,
  memberModalSecondaryButtonClassName,
} from "./memberModalFooterButtonClasses";
import { IPerson } from "../../../interfaces/models/IPerson";
import type { IPersonView } from "../../../interfaces/models/IPersonView";
import type { IAreaOfPractice } from "../../../interfaces/models/IAreaOfPractice";
import { getAOPsByFirm } from "../../../Services/AreaOfPractice";
import { updatePerson } from "../../../Services/Person";
import {
  ipersonToPersonCreate,
  mapPersonCreateToSavePayload,
} from "../../Organismos/Person/personForm/mapPerson";
import CheckBoxGroup from "../../staff/CheckBoxGroup";
import CustomScrollContainer from "../../Atomos/CustomScrollContainer";
import DragPanIcon from "../../Atomos/Icons/Drag_pan";
import { toast } from "react-toastify";
import { aopRankNamesPatchFromOrder } from "../../../utils/aopRankNames";

const MODAL_PAD = "px-6 md:px-8";

const MAX_MY_AOP = 4;

type EditAreasOfPracticeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  person: IPerson;
  onSaved: () => void;
};

function SortableExpertiseRow({ id, label }: { id: string; label: string }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex w-full min-w-0 items-center gap-3 rounded-md bg-gray-25 px-3 py-2 shadow-sm"
    >
      <button
        type="button"
        className="touch-none shrink-0 cursor-grab p-0.5 text-gray-font hover:text-gray-800 active:cursor-grabbing"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <DragPanIcon size={18} />
      </button>
      <span className="min-w-0 text-sm font-bold leading-snug text-gray-font">{label}</span>
    </div>
  );
}

const EditAreasOfPracticeModal: React.FC<EditAreasOfPracticeModalProps> = ({
  isOpen,
  onClose,
  person,
  onSaved,
}) => {
  /** Firm catalog only ({@link getAOPsByFirm}); person AOPs not on the firm list are omitted from the UI. */
  const [firmAops, setFirmAops] = useState<IAreaOfPractice[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [areasIds, setAreasIds] = useState<string[]>([]);
  const [myAOPIds, setMyAOPIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  const aopById = useMemo(() => {
    const m = new Map<string, IAreaOfPractice>();
    for (const a of firmAops) {
      if (a.id) {
        m.set(a.id, a);
      }
    }
    return m;
  }, [firmAops]);

  const hydrateFromPerson = useCallback(() => {
    const form = ipersonToPersonCreate(person);
    const aop = normalizeIdList(form.areasOfPractice);
    const myAop = normalizeIdList(form.aopExpertiseIds).slice(0, MAX_MY_AOP);
    setAreasIds(aop);
    setMyAOPIds(myAop);
  }, [person]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    hydrateFromPerson();
  }, [isOpen, hydrateFromPerson]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!person.firm?.trim()) {
      setFirmAops([]);
      return;
    }
    let cancelled = false;
    const firmId = person.firm.trim();

    setCatalogLoading(true);
    void getAOPsByFirm(firmId)
      .then((firmRows) => {
        if (cancelled) {
          return;
        }
        const firm = Array.isArray(firmRows)
          ? [...firmRows].sort((x, y) =>
              x.displayName.localeCompare(y.displayName, undefined, {
                sensitivity: "base",
              })
            )
          : [];
        const firmIds = new Set(
          firm.map((a) => a.id).filter((id): id is string => !!id?.trim())
        );
        setFirmAops(firm);
        setAreasIds((prev) => prev.filter((id) => firmIds.has(id)));
        setMyAOPIds((prev) => prev.filter((id) => firmIds.has(id)));
      })
      .catch(() => {
        if (!cancelled) {
          setFirmAops([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, person.firm]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const poolAops = useMemo(() => {
    const ids = new Set(areasIds);
    return firmAops
      .filter((a) => ids.has(a.id))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [areasIds, firmAops]);

  const handleAreasOfPracticeChange = useCallback(
    (nextIds: string[]) => {
      const prunedMy = myAOPIds.filter((id) => nextIds.includes(id));
      setAreasIds(nextIds);
      setMyAOPIds(prunedMy);
    },
    [myAOPIds]
  );

  const handleMyAOPChange = useCallback(
    (next: string[]) => {
      const capped = next.slice(0, MAX_MY_AOP);
      setMyAOPIds(capped);
    },
    []
  );

  const handleExpertiseDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const items = [...myAOPIds];
      const oldIndex = items.indexOf(String(active.id));
      const newIndex = items.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }
      setMyAOPIds(arrayMove(items, oldIndex, newIndex));
    },
    [myAOPIds]
  );

  const handleSave = async () => {
    if (!person.id?.trim()) {
      toast.error("Missing profile id.");
      return;
    }
    if (!areasIds.length) {
      toast.error("Select at least one area of practice.");
      return;
    }
    if (!myAOPIds.length) {
      toast.error('Choose at least one entry in "My Areas of Practice".');
      return;
    }

    setSaving(true);
    try {
      const base = ipersonToPersonCreate(person);
      const form = {
        ...base,
        areasOfPractice: areasIds,
        aopExpertiseIds: myAOPIds,
        ...aopRankNamesPatchFromOrder(myAOPIds, aopById),
      };
      const payload = mapPersonCreateToSavePayload(form, {
        id: person.id,
      } as IPersonView);
      await updatePerson(payload);
      toast.success("Areas of practice updated.");
      onSaved();
      onClose();
    } catch {
      toast.error("Could not save areas of practice.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const card = (
    <div
      className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-labelledby="edit-aop-modal-title"
    >
        <div
          className={`flex shrink-0 items-start justify-between gap-4 ${MODAL_PAD} pt-6 md:pt-8`}
        >
          <h2
            id="edit-aop-modal-title"
            className="font-decimal text-2xl font-semibold text-primary"
          >
            Areas of Practice
          </h2>
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center text-gray-font hover:text-gray-600"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            <CloseModal />
          </button>
        </div>

        <div
          className={`min-h-0 flex-1 overflow-y-auto ${MODAL_PAD} pb-4 pt-6 md:pt-6`}
        >
          {catalogLoading ? (
            <p className="text-sm text-gray-500">Loading areas of practice…</p>
          ) : (
            <div className="edit-aop-modal-checkboxes grid w-full grid-cols-1 gap-6 divide-gray-200 lg:grid-cols-12 lg:gap-0 lg:divide-x">
              <div className="flex min-w-0 flex-col gap-3 lg:col-span-5 lg:pr-6">
                <div className="flex flex-row gap-2 items-center">
                  <h3 className="text-lg font-bold text-gray-font min-w-0">
                    Select Areas of Practice
                  </h3>
                  <p className="text-xs text-gray-500 min-w-0 shrink-0">
                    ({firmAops.length} AOPs)
                  </p>
                </div>

                <div
                  className="w-full min-w-0"
                  aria-label="Areas of practice list, scroll for more options"
                >
                  <CustomScrollContainer
                    direction="vertical"
                    showScrollbar="auto"
                    className="h-[220px] min-h-0 p-2 sm:h-[280px] lg:h-[340px]"
                  >
                    <CheckBoxGroup<IAreaOfPractice>
                      contentClassName="grid grid-cols-1 gap-2 sm:grid-cols-2"
                      options={firmAops}
                      value={areasIds}
                      onChange={handleAreasOfPracticeChange}
                      getOptionValue={(a) => a.id ?? ""}
                      getOptionLabel={(a) => a.displayName}
                    />
                  </CustomScrollContainer>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:col-span-7 lg:pl-6">
                <div className="flex min-h-0 min-w-0 flex-col gap-3">
                  <h3 className="text-lg font-bold text-gray-font">My Areas of Practice</h3>
                  <p className="text-xs text-gray-500">
                    Select up to {MAX_MY_AOP} areas for your profile emphasis.
                  </p>
                  <CustomScrollContainer
                    direction="vertical"
                    showScrollbar="auto"
                    className="min-h-[200px] max-h-[320px]"
                  >
                    <CheckBoxGroup<IAreaOfPractice>
                      contentClassName="flex flex-col gap-2"
                      options={poolAops}
                      value={myAOPIds}
                      onChange={handleMyAOPChange}
                      getOptionValue={(a) => a.id ?? ""}
                      getOptionLabel={(a) => a.displayName}
                      getOptionDisabled={(a) => {
                        const id = a.id ?? "";
                        return myAOPIds.length >= MAX_MY_AOP && !myAOPIds.includes(id);
                      }}
                    />
                  </CustomScrollContainer>
                </div>

                <div className="flex min-w-0 flex-col gap-3">
                  <h3 className="text-lg font-bold text-gray-font">
                    My Expertise
                  </h3>
                  <p className="text-xs text-gray-500">
                    (Select max. {MAX_MY_AOP} Areas of Practice)
                  </p>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleExpertiseDragEnd}
                  >
                    {myAOPIds.length > 0 ? (
                      <SortableContext
                        items={myAOPIds}
                        strategy={verticalListSortingStrategy}
                      >
                        <ul className="m-0 flex list-none flex-col gap-3 p-0">
                          {myAOPIds.map((id, index) => (
                            <li
                              key={id}
                              className="grid grid-cols-[auto_1fr] items-start gap-3"
                            >
                              <span className="w-6 pt-2 text-center text-sm font-medium text-gray-font">
                                {index + 1}.
                              </span>
                              <SortableExpertiseRow
                                id={id}
                                label={
                                  (aopById.get(id)?.displayName ?? "").trim() ||
                                  "Area of practice"
                                }
                              />
                            </li>
                          ))}
                        </ul>
                      </SortableContext>
                    ) : (
                      <p className="text-sm text-gray-400">
                        Select areas in &quot;My Areas of Practice&quot; to set expertise order.
                      </p>
                    )}
                  </DndContext>

                  {myAOPIds.length > 0 ? (
                    <p className="text-xs text-gray-500">
                      * To change order, click on the drag icon and drag the element to a new spot
                      within the list.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className={`flex flex-wrap justify-end gap-3 border-t border-gray-200 ${MODAL_PAD} py-5`}
        >
          <button
            type="button"
            className={memberModalSecondaryButtonClassName}
            onClick={onClose}
            disabled={saving}
          >
            <span>Cancel</span>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-font">
              <CancelIcon className="h-4 w-4" />
            </span>
          </button>
          <button
            type="button"
            className={memberModalPrimaryButtonClassName}
            onClick={() => void handleSave()}
            disabled={saving || catalogLoading}
          >
            <span>Update Information</span>
            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-white [&_svg]:fill-current">
              <SaveChanges />
            </span>
          </button>
        </div>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      onClick={saving ? undefined : onClose}
      role="presentation"
    >
      {card}
    </div>,
    document.body
  );
};

function normalizeIdList(ids: string[] | undefined): string[] {
  return (ids ?? [])
    .filter((id) => typeof id === "string" && id.trim())
    .map((id) => id.trim());
}

export default EditAreasOfPracticeModal;
